import {
  FieldTimeOutlined,
  InfoCircleOutlined,
  LoadingOutlined,
  SearchOutlined,
  ThunderboltOutlined,
  UserSwitchOutlined,
} from '@ant-design/icons';
import {
  Button,
  Card,
  Drawer,
  Flex,
  Input,
  Table,
  theme,
  Timeline,
  Tooltip,
} from 'antd';
import { TimelineItemProps } from 'antd/lib';
import { TableDateTimeFormat } from 'constants/dateFormats';
import dayjs from 'dayjs';
import { AuditLog as AuditEntry } from 'interfaces/journey';
import React, { ReactNode, useMemo, useState } from 'react';
import { useAuditLog } from 'services/journeys';

interface AuditLogProps {
  journeyId?: string;
  mode: 'list' | 'dashboard';
}

const normalize = (text: string) =>
  text
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();

const auditHaystack = (audit: AuditEntry) =>
  normalize(
    [
      audit.message,
      audit.performedBy,
      audit.action,
      ...Object.entries(audit.extras ?? {}).map(
        ([key, value]) =>
          `${key} ${
            typeof value === 'object' ? JSON.stringify(value) : String(value)
          }`,
      ),
    ]
      .filter(Boolean)
      .join(' '),
  );

const getAuditOwnerIcon = (performer?: string) => {
  const style = { fontSize: '14px', color: 'gray' };
  if (performer === 'driver') return <UserSwitchOutlined style={style} />;
  if (performer === 'system') return <ThunderboltOutlined style={style} />;
};

const ExtrasLog = ({
  title,
  extras,
}: {
  title: string;
  extras: Record<string, JSON>;
}) => {
  const [open, setOpen] = useState<boolean>(false);

  const extrasArray = useMemo(
    () =>
      Object.entries(extras).map(([key, value]) => ({
        info: key,
        detail: Array.isArray(value) ? value.join(', ') : value,
      })),
    [extras],
  );

  const labelColumn = {
    key: 'info',
    dataIndex: 'info',
    ellipsis: true,
    width: '33%',
  };

  const detailColumn = {
    key: 'detail',
    dataIndex: 'detail',
  };

  return (
    <>
      <Drawer open={open} onClose={() => setOpen(false)} title={title}>
        <Table
          pagination={false}
          size="small"
          bordered
          showHeader={false}
          columns={[labelColumn, detailColumn]}
          dataSource={extrasArray}
        />
      </Drawer>
      <InfoCircleOutlined
        style={{ marginLeft: '8px', color: 'blue' }}
        onClick={() => setOpen(true)}
      />
    </>
  );
};

const AuditLog = React.forwardRef<HTMLDivElement, AuditLogProps>(
  ({ journeyId, mode }, ref) => {
    const { token } = theme.useToken();
    const [open, setOpen] = useState<boolean>(false);
    const [search, setSearch] = useState('');
    const { data: auditLog, isLoading } = useAuditLog(journeyId, open);

    const indexedLog = useMemo(
      () =>
        (auditLog ?? []).map((audit) => ({
          audit,
          haystack: auditHaystack(audit),
        })),
      [auditLog],
    );
    const filteredLog = useMemo(() => {
      const words = normalize(search).split(/\s+/).filter(Boolean);
      return indexedLog
        .filter(({ haystack }) =>
          words.every((word) => haystack.includes(word)),
        )
        .map(({ audit }) => audit);
    }, [indexedLog, search]);

    let emptyText: string | undefined;
    if (!auditLog || auditLog.length === 0) {
      emptyText = 'No se registran cambios';
    } else if (filteredLog.length === 0) {
      emptyText = 'Sin auditorías que coincidan';
    }

    return (
      <>
        {journeyId && (
          <Drawer
            title="Registro de auditorias"
            onClose={() => {
              setOpen(false);
              setSearch('');
            }}
            open={open}
            loading={isLoading}
            zIndex={1000}
            width={480}
            styles={{ body: { paddingTop: 0 } }}
          >
            <div
              style={{
                position: 'sticky',
                top: 0,
                zIndex: 1,
                background: token.colorBgElevated,
                paddingTop: '12px',
                paddingBottom: '24px',
              }}
            >
              <Input
                allowClear
                prefix={<SearchOutlined />}
                placeholder="Buscar en las auditorías"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            {emptyText ?? (
              <Timeline
                items={filteredLog.map((audit) => {
                  const descriptor = audit.message ?? 'Acción desconocida';
                  return {
                    children: (
                      <>
                        <i style={{ fontSize: '12px' }}>
                          {dayjs(audit.timestamp).format(TableDateTimeFormat)}
                        </i>
                        <br />
                        <b style={{ fontSize: '18px' }}>{descriptor}</b>
                        {audit.extras &&
                          Object.keys(audit.extras).length > 0 && (
                            <ExtrasLog
                              title={descriptor}
                              extras={audit.extras}
                            />
                          )}
                        <br />
                        {audit.performedBy && (
                          <Flex gap={4} align="center">
                            {getAuditOwnerIcon(audit.performer)}
                            <p style={{ fontSize: '14px', color: 'gray' }}>
                              {audit.performedBy}
                            </p>
                          </Flex>
                        )}
                      </>
                    ) as ReactNode,
                  } as TimelineItemProps;
                })}
              />
            )}
          </Drawer>
        )}
        {mode === 'dashboard' ? (
          <Card
            ref={ref}
            bodyStyle={{
              padding: '6px',
            }}
          >
            <Button
              style={{ width: '100%' }}
              icon={
                isLoading ? (
                  <LoadingOutlined style={{ fontSize: '24px' }} />
                ) : (
                  <FieldTimeOutlined style={{ fontSize: '24px' }} />
                )
              }
              onClick={() => setOpen(true)}
              disabled={isLoading}
              size="large"
              type="text"
            />
          </Card>
        ) : (
          <Tooltip title="Auditoría" zIndex={5}>
            {isLoading ? (
              <LoadingOutlined
                style={{ color: 'grey', fontSize: 18 }}
                disabled
              />
            ) : (
              <FieldTimeOutlined
                onClick={() => setOpen(true)}
                style={{ color: token.colorPrimary, fontSize: 18 }}
              />
            )}
          </Tooltip>
        )}
      </>
    );
  },
);

export default AuditLog;
